import React, { useEffect, useState } from 'react';
import { pick, prop } from 'ramda';
import { Helmet } from 'react-helmet-async';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { IPageState } from 'js/components/system/Page/store/types';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

import * as Types from './types';
import * as helpers from '../../helpers';
import { SEO_LINK_TYPE } from '../../constants';

export const PageSEOTags: React.FC<{ page?: IPageState; seoLinkType: SEO_LINK_TYPE }> = ({
    page,
    seoLinkType = SEO_LINK_TYPE.PAGE_URL
}) => {
    const lang = useUserSelector(prop('lang'));
    const { allowedLocales, defaultLocale, seo: { useTrailingSlash } } = useAppSelector(pick(['allowedLocales', 'defaultLocale', 'seo']));
    const [dynamicTags, setDynamicTags] = useState<Types.IDynamicTags | null>(null);

    useEffect(() => {
        if (!page) {
            return;
        }

        publicEventsAPIService.on('page:update:seoTags', setDynamicTags);

        return () => {
            setDynamicTags(null);
            publicEventsAPIService.off('page:update:seoTags', setDynamicTags);
        };
    }, [page]);

    if (!page) {
        return null;
    }

    const alternateLinks = helpers.getAlternateLinks({ page, allowedLocales, defaultLocale, useTrailingSlash, seoLinkType });
    const canonicalLinkUrl = helpers.getCanonicalLinkUrl({ page, defaultLocale, lang, useTrailingSlash, seoLinkType });
    const pageTags = {
        ...pick(['title', 'robots', 'keywords', 'description'], page),
        canonical: canonicalLinkUrl
    };
    const { title, robots, keywords, description, canonical } = Object.entries(dynamicTags || {})
        .reduce((acc, [tag, value]) => {
            if (typeof value === 'string' || value === null) {
                acc[tag as keyof Types.TPageTags] = value || '';
            }

            return acc;
        }, { ...pageTags });

    return (
        <Helmet>
            {SEO_LINK_TYPE.DISABLED !== seoLinkType && (
                <>
                    <title>{title}</title>
                    <meta name="robots" content={robots} />
                    <meta name="keywords" content={keywords} />
                    <meta name="description" content={description} />
                    <meta name="referrer" content="no-referrer-when-downgrade" />
                </>
            )}
            {alternateLinks.map(alternateLink => (
                <link
                    rel={'alternate'}
                    href={alternateLink.href}
                    key={alternateLink.hrefLang}
                    hrefLang={alternateLink.hrefLang}
                />
            ))}
            {canonical && (<link rel="canonical" href={canonical} />)}
        </Helmet>
    );
};
